import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainScreen } from '@/components/shared/MainScreen/MainScreen';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button, Card, CardContent } from '@/components/ui';
import { ProgressSegmented } from '@/components/ui/ProgressSegmented';
import { useProjectViews } from '@/hooks/useProjectViews';
import { useAuthStore } from '@/stores/authStore';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import {
  executionMilestones,
  PROJECT_STATUS_LABELS,
  projectProgress,
  projectStatus,
} from '@/lib/project-presentation';
import type { ProjectView } from '@/types/current-project';

const stateColors: Record<ReturnType<typeof projectStatus>, string> = {
  Cancelled: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  'Dispute open': 'bg-red-500/15 text-red-400 border-red-500/30',
  Completed: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  'Execution in progress': 'bg-green-500/15 text-green-400 border-green-500/30',
  'Proposal awaiting approval':
    'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'Awaiting planning quote':
    'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  'Planning quote ready':
    'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  'Planning in progress': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'Planning delivery ready':
    'bg-purple-500/15 text-purple-400 border-purple-500/30',
  'Planning complete': 'bg-gray-500/15 text-gray-400 border-gray-500/30',
};

export default function ProjectsPage() {
  const query = useProjectViews();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const isClientView = useAuthStore((state) => state.user?.role === 'client');
  const { emptyStateWorkerText } = useDaoConfig();
  const projects = (query.data ?? []).filter(
    (project) => filter === 'all' || projectStatus(project) === filter
  );
  const newProject = () => navigate('/projects/new');
  return (
    <MainScreen
      headerContent={
        <div className="font-inter flex flex-col gap-6 text-left lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-1 items-center gap-4">
            <div>
              <h1 className="mb-1 text-[30px] font-bold leading-[42px]">
                Projects
              </h1>
              <p className="mb-2 text-base text-[--text-dark-secondary]">
                Find out the progress your projects, documents, milestones and
                the latest activities.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                disabled
                title="Project documentation is not available yet."
                className="inline-flex items-center gap-1 text-base font-semibold text-[--state-brand-active]"
              >
                Learn more about projects{' '}
                <i aria-hidden="true" className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
          {isClientView && (
            <Button className="h-11 gap-2 px-6 text-lg" onClick={newProject}>
              <i aria-hidden="true" className="ri-add-line" /> New Project
            </Button>
          )}
        </div>
      }
      contentClassName="pageContent text-left"
    >
      {query.isPending ? (
        <div role="status" aria-label="Loading projects">
          <span className="sr-only">Loading projects…</span>
          <div
            className="grid animate-pulse grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
            aria-hidden="true"
          >
            {Array.from({ length: 6 }, (_, index) => (
              <div
                key={index}
                className="rounded-xl border border-[--base-border] bg-[--base-surface-2] p-6"
              >
                <div className="mb-6 ml-auto h-7 w-40 rounded-md bg-[--base-fill-2]" />
                <div className="mb-4 h-9 rounded-md bg-[--base-fill-2]" />
                <div className="h-3 rounded bg-[--base-fill-2]" />
              </div>
            ))}
          </div>
        </div>
      ) : query.isError ? (
        <Card className="mx-auto max-w-2xl">
          <CardContent className="p-8 text-center" role="alert">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/15">
              <i
                aria-hidden="true"
                className="ri-error-warning-line text-2xl text-red-400"
              />
            </div>
            <h2 className="mb-2 text-xl font-semibold">
              Failed to load projects
            </h2>
            <p className="mb-4 text-[--text-dark-secondary]">
              {query.error.message}
            </p>
            <Button variant="outline" onClick={() => void query.refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : !query.data.length ? (
        <div className="text-center">
          <EmptyState
            icon="ri-folder-line"
            title="No projects yet"
            description={
              isClientView
                ? 'Create your first project proposal to get started.'
                : emptyStateWorkerText
            }
            action={
              isClientView
                ? { label: 'New Project', onClick: newProject }
                : undefined
            }
          />
        </div>
      ) : (
        <div>
          <div className="mb-6 flex items-center gap-3 text-sm text-[--text-dark-secondary]">
            <label htmlFor="state-filter">Filter by state:</label>
            <select
              id="state-filter"
              className="h-9 rounded-md border border-[--base-border] bg-[--base-surface-2] px-3 text-[--text-dark-primary] [color-scheme:dark] focus:ring-2 focus:ring-[--state-brand-active]"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            >
              <option value="all">All States</option>
              {PROJECT_STATUS_LABELS.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
            <span className="ml-auto" role="status">
              {projects.length} of {query.data.length} project
              {query.data.length === 1 ? '' : 's'}
            </span>
          </div>
          {!projects.length ? (
            <div className="py-12 text-center">
              <p>No projects match the selected filter.</p>
              <Button
                variant="ghost"
                className="mt-3"
                onClick={() => setFilter('all')}
              >
                Clear Filter
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {projects.map((project) => (
                <ProjectCard
                  key={project.projectId}
                  project={project}
                  onClick={() => navigate('/projects/' + project.projectId)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </MainScreen>
  );
}

function ProjectCard({
  project,
  onClick,
}: {
  project: ProjectView;
  onClick: () => void;
}) {
  const progress = projectProgress(project);
  const pendingTasks = executionMilestones(project).reduce(
    (count, milestone) =>
      count +
      milestone.taskStorage.tasks.filter(
        ({ task }) => task.status !== 'Done' && task.status !== 'Closed'
      ).length,
    0
  );
  return (
    <button
      type="button"
      aria-label={'Open project: ' + project.title}
      aria-describedby={project.projectId + '-summary'}
      onClick={onClick}
      className="font-inter flex flex-col rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] p-[--spacing-10] text-left shadow-elevation-sm transition-colors hover:border-[--state-brand-active] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[--state-brand-active]"
    >
      <span id={project.projectId + '-summary'} className="sr-only">
        {projectStatus(project)}. {pendingTasks} pending tracking tasks.{' '}
        {progress === null
          ? 'Execution has not started.'
          : progress + '% of execution milestones accepted.'}
      </span>
      <div className="mb-4 flex w-full flex-wrap items-center justify-between gap-2">
        {pendingTasks > 0 && (
          <span className="rounded-xl bg-[--state-success-active] px-3 py-1 text-base font-semibold text-[--text-light-primary]">
            {pendingTasks} Pending Task{pendingTasks === 1 ? '' : 's'}
          </span>
        )}
        <span
          className={
            'ml-auto inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ' +
            stateColors[projectStatus(project)]
          }
        >
          {projectStatus(project)}
        </span>
      </div>
      <h3 className="mb-4 line-clamp-2 text-center text-2xl font-semibold leading-[38px]">
        {project.title}
      </h3>
      <div
        role="progressbar"
        aria-label={project.title + ' accepted milestones'}
        aria-valuenow={progress ?? 0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={
          progress === null
            ? 'Execution has not started'
            : progress + '% of execution milestones accepted'
        }
        className="mt-auto w-full space-y-2"
      >
        <ProgressSegmented value={progress ?? 0} showLabel={false} />
        <div className="flex justify-end">
          <span className="font-inter text-base font-medium text-[--text-dark-secondary]">
            {progress ?? 0}%
          </span>
        </div>
      </div>
    </button>
  );
}
